/**
 * Read-only detail typography scale:
 * - DetailSectionTitle — drawer / panel section grouping
 * - DetailItemTitle — name inside nested cards (line items, list entries)
 * - DetailField — label (muted) + value stack
 * - DetailMetaRow — inline label + value (e.g. team, progress, quantity)
 * - DetailValue — value-only wrapper for custom layouts
 *
 * Labels use FieldLabel variant="detail"; values use detailValue* tokens from heading.tsx.
 */
import type { ReactElement, ReactNode } from "react";

import {
  elevatedSurfaceClassName,
  formFlowSurfaceClassName,
} from "@/components/forms/formFlowLayout";
import { cellMainBoxClassName } from "@/components/ui/cell-main";
import {
  detailLabelClassName,
  detailSecondaryTextClassName,
  detailTertiaryTextClassName,
  detailValueClassName,
  detailValueEmphasisClassName,
  detailValueMutedClassName,
  FieldLabel,
  Heading,
  type HeadingVariant,
} from "@/components/ui/heading";
import { cn } from "@/lib/utils";

/** Read-only detail panel — same surface chrome as FormFlow edit body. */
export const detailBoxClassName = formFlowSurfaceClassName;

/** Bordered section surface inside drawers (service fronts, occurrences, materials). */
export const detailSectionClassName = cn(elevatedSurfaceClassName, "p-4");

/** Scroll body for detail drawers — top padding keeps section borders from clipping under the header. */
export const detailDrawerScrollBodyClassName = "min-h-0 flex-1 overflow-y-auto px-4 py-4";

/** Vertical spacing between bordered section cards inside detail drawers. */
export const detailDrawerSectionsClassName = "space-y-3";

/** Flex gap between section cards in multi-column overview-style layouts. */
export const detailSectionsGapClassName = "gap-3";

/** Padding between section title and body content. */
export const detailSectionBodyClassName = "min-w-0 pt-2";

/** Vertical spacing for stacked blocks inside a section card. */
export const detailSectionInnerStackClassName = "space-y-2";

/** Nested read-only line box — same surface as {@link cellMainBoxClassName}. */
export const detailNestedCardClassName = cellMainBoxClassName;

type DetailBoxLabelProps = {
  children: ReactNode;
  className?: string;
  as?: "div" | "dt" | "p" | "span";
};

export function DetailBoxLabel({
  children,
  className,
  as: Tag = "div",
}: DetailBoxLabelProps): ReactElement {
  return (
    <FieldLabel variant="detail" as={Tag} className={className}>
      {children}
    </FieldLabel>
  );
}

type DetailSectionTitleProps = {
  children: ReactNode;
  className?: string;
  /** Semantic level inside drawers (defaults to h3). */
  level?: 2 | 3 | 4;
  variant?: Extract<HeadingVariant, "section" | "subsection">;
};

export function DetailSectionTitle({
  children,
  className,
  level = 3,
  variant = "subsection",
}: DetailSectionTitleProps): ReactElement {
  return (
    <Heading variant={variant} level={level} className={className}>
      {children}
    </Heading>
  );
}

type DetailItemTitleProps = {
  children: ReactNode;
  className?: string;
  as?: "p" | "span" | "div";
};

/** Name/title inside a nested read-only card (line item, material row). */
export function DetailItemTitle({ children, className, as }: DetailItemTitleProps): ReactElement {
  return (
    <Heading variant="item" level={4} as={as} className={className}>
      {children}
    </Heading>
  );
}

type DetailValueProps = {
  children: ReactNode;
  className?: string;
  emphasis?: boolean;
  muted?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  as?: "p" | "span" | "div";
};

/** Value-only wrapper applying the read-only value scale. */
export function DetailValue({
  children,
  className,
  emphasis = false,
  muted = false,
  secondary = false,
  tertiary = false,
  as: Tag = "div",
}: DetailValueProps): ReactElement {
  return (
    <Tag
      className={cn(
        tertiary
          ? detailTertiaryTextClassName
          : muted
            ? detailValueMutedClassName
            : secondary
              ? detailSecondaryTextClassName
              : emphasis
                ? detailValueEmphasisClassName
                : detailValueClassName,
        className,
      )}
    >
      {children}
    </Tag>
  );
}

type DetailMetaRowProps = {
  label: ReactNode;
  children: ReactNode;
  className?: string;
  valueClassName?: string;
};

/** Inline metadata row: muted label + foreground value (team, progress, quantity). */
export function DetailMetaRow({
  label,
  children,
  className,
  valueClassName,
}: DetailMetaRowProps): ReactElement {
  return (
    <p className={cn("text-sm", className)}>
      <span className={detailLabelClassName}>{label}: </span>
      <span className={cn("text-foreground", valueClassName)}>{children}</span>
    </p>
  );
}

type DetailFieldProps = {
  label: ReactNode;
  children: ReactNode;
  muted?: boolean;
  emphasis?: boolean;
  className?: string;
};

export function DetailField({
  label,
  children,
  muted,
  emphasis,
  className,
}: DetailFieldProps): ReactElement {
  return (
    <div className={className}>
      <DetailBoxLabel>{label}</DetailBoxLabel>
      <DetailValue className="mt-1" emphasis={emphasis} muted={muted}>
        {children}
      </DetailValue>
    </div>
  );
}
